/**
 * Broker (SpotMe Pro) base variant.
 *
 * Used for `DealType.Broker` listings — high-value commercial spaces,
 * industrial warehouses, and parking fleets routed through a specialist.
 *
 * 2026-05-26 course-correction (docs/plans/2026-05-26-001): the primary
 * CTA is now a WhatsApp pill that routes directly to the SpotMe corporate
 * WhatsApp via `buildWhatsappUrl(message)` — no LeadForm in front. The
 * 2 weeks of production PostHog data following the previous overhaul
 * showed the inline LeadForm converted at 0.6-0.8% engagement→submit
 * while the WhatsApp pill converted at ~75% click→qualified-lead. The
 * LeadForm is still mounted for the email-secondary path (desktop
 * outlined button + mobile inline body block in `LeadFormInlineCta`) —
 * it just isn't the primary surface anymore. Fleet pill on Monthly+Parking
 * listings is unaffected (still uses the inline form via
 * `variantOverride={baseFleetVariant}`).
 */

import type { BaseListingVariant } from 'src/lib/listingVariants/types'

export const baseBrokerVariant: BaseListingVariant = {
  priceCard: {
    primaryCta: {
      labelKey: 'CTA.WHATSAPP_BROKER',
      action: 'open-whatsapp'
    },
    secondaryCta: null,
    // Broker has no scarcity chip — the recent-inquiry count that would
    // have driven "Recibió N propuestas esta semana" was never wired in
    // production and the locale key was removed. PriceCard already gates
    // the chip render on variantKey, so this flag staying false is the
    // source of truth.
    showScarcity: false,
    scarcityCopyKey: null,
    showWatching: true,
    showStorageTimeChips: false,
    showQuantityInput: false,
    showFleetCta: false,
    showPriceBreakdown: false,
    trustSignals: [
      'broker.no_commitment',
      'broker.specialist',
      'broker.custom_terms'
    ]
  },
  leadForm: {
    // 2026-05-26: the variant's primary CTA is no longer the LeadForm
    // (PriceCard renders the WhatsApp pill directly). This config is
    // still consumed by the secondary "Solicitar por correo" path
    // (desktop outlined button + mobile inline body block) which opens
    // the same LeadForm in a modal drawer. `enabled: true` keeps the
    // drawer functional — PriceCard's inline-on-card branch is gated
    // by `priceCard.primaryCta.action === 'open-whatsapp'` instead.
    enabled: true,
    desktopContainer: 'inline-on-card',
    primaryChannel: 'whatsapp',
    // Step order INVERTED 2026-05-18: contact first, qualifier (now
    // role-shifted to OPTIONAL enrichment) second, success last. The
    // qualifier-first order was filtering qualified intent and tanking
    // submit rates to ~0-2%. Pre-creates the HubSpot deal at Step 1
    // submit so partial completers still land in sales' inbox. The
    // step NAME stays 'qualifier' to avoid type-level churn — its
    // semantic role is now "optional enrichment".
    steps: ['contact', 'qualifier', 'success'],
    fields: [
      { name: 'name', required: true },
      { name: 'phone', required: true },
      { name: 'email', required: true },
      { name: 'additionalInfo', required: false }
    ],
    qualifierField: 'sqm',
    specialistKey: 'fernando',
    responseBadge: { show: true },
    whatsappRecipient: 'specialist',
    method: 'broker',
    leadType: 'demand',
    subType: 'broker_contact',
    postSubmit: 'create-account-prompt',
    // An asesor de SpotMe genuinely calls on Broker — keep the existing
    // Step-3 copy. The Traditional set would tell a B2B renter that an
    // operator we never handed them to is about to phone them.
    leadCopyKey: 'default'
  },
  sections: {
    user: { variant: 'specialist' },
    size: { show: true, framing: 'b2b' },
    // Chubb is on-platform-checkout only (Anexo A scope). Broker deals
    // are off-platform (Anexo B / SpotMe Pro) — no insurance product is
    // bundled at signature. Hide the badge so we don't paraphrase a
    // coverage we aren't selling on this surface.
    insurance: { show: false, mode: 'hidden' },
    reviews: { show: true },
    refundBadge: { show: false },
    similarListings: { show: true, tier: 'same' },
    securityAndSupport: {
      headlineKey: 'SECURITY.BROKER.TITLE_PREFIX',
      accentKey: 'SECURITY.BROKER.TITLE_ACCENT',
      subtitleKey: 'SECURITY.BROKER.SUBTITLE',
      cardKeys: [
        'verification_b2b',
        'anti_fraud',
        'custom_terms_b2b',
        'support_specialist_b2b'
      ]
    }
  },
  checkout: {
    authGate: 'none',
    guestEmailOk: false
  }
}
